// src/pages/auth/Login.jsx
// Updated: robust API base for Vite + safer forgot-password call (email OR employee code)
// Keeps HR-themed animated background using the resolved tenant brand colors.

import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { loginUser, resetAuthStatus } from "../../features/auth/authSlice";
import { resendMfaCode, setMfaDeviceToken, verifyMfaCode } from "../../features/auth/authAPI";
import logo from "./MGI.png";

import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Stack,
  Divider,
  IconButton,
  InputAdornment,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Checkbox,
  FormControlLabel,
} from "@mui/material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { CV_TOUR_SEEN_KEY } from "../../components/onboarding/CvProductTour";
import { CV_ADMIN_REVIEW_TOUR_SEEN_KEY } from "../../components/onboarding/CvAdminReviewTour";
import { useTenant } from "../../tenancy/TenantProvider";
import { getApiBase } from "../../config/env";
import { saveAuthFromResponse } from "../../services/api";
import { SearchableSelect } from "../../components/ui";

// HR “modules” icons
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";

const API_BASE = getApiBase();

async function safeJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

const normalizeHex = (value, fallback) => {
  const text = String(value || "").trim();
  const hex = text.startsWith("#") ? text : `#${text}`;
  return /^#[0-9A-Fa-f]{6}$/.test(hex) ? hex : fallback;
};

const hexToRgba = (hex, alphaValue) => {
  const normalized = normalizeHex(hex, "#000000").slice(1);
  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);
  return `rgba(${red},${green},${blue},${alphaValue})`;
};

function Login() {
  const tenantContext = useTenant();
  const { branding } = tenantContext;
  const [employeeCode, setEmployeeCode] = useState("");
  const [password, setPassword] = useState("");
  const [orgCode, setOrgCode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("org_code") ||
      params.get("org") ||
      localStorage.getItem("last_login_org_code") ||
      ""
    );
  });
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [orgOptionsLoading, setOrgOptionsLoading] = useState(false);
  const [orgOptionsError, setOrgOptionsError] = useState("");
  const [showPass, setShowPass] = useState(false);

  // Forgot password
  const [fpOpen, setFpOpen] = useState(false);
  const [fpIdentifier, setFpIdentifier] = useState(""); // email OR employee code
  const [fpOrgCode, setFpOrgCode] = useState("");        // WEB-005: org for forgot password
  const [fpLoading, setFpLoading] = useState(false);

  // Two-step login: when the tenant has email MFA on, a correct password
  // returns a challenge instead of a session and we swap this card to the code
  // form. Kept as page state rather than a route or a dialog — the animated
  // background and tenant resolution must survive, and a dialog is dismissable
  // by backdrop click, which is wrong for a mandatory step.
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState("");
  const [mfaResendIn, setMfaResendIn] = useState(0);
  const [trustDevice, setTrustDevice] = useState(false);
  const inMfaStep = mfaChallenge !== null;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, error } = useSelector((state) => state.auth);
  const isLoading = status === "loading";

  const brandPrimary = normalizeHex(branding.primary_color, "#1f3d7c");
  const brandSecondary = normalizeHex(branding.secondary_color, "#2389ba");
  const brandAccent = normalizeHex(branding.accent_color, "#75913b");
  const styles = useMemo(
    () => createLoginStyles({ primary: brandPrimary, secondary: brandSecondary, accent: brandAccent }),
    [brandAccent, brandPrimary, brandSecondary]
  );
  const loginLogo = branding.logo_light_url || logo;
  const brandName = branding.display_name || "HR Workspace";
  const productLabel = branding.product_label || "HR workspace";
  const tenantOrgCode = String(tenantContext?.tenant?.org_code || "").trim().toLowerCase();
  const resolveTenantByOrgCode = tenantContext?.resolveTenantByOrgCode;
  const selectedOrganization = useMemo(
    () => organizationOptions.find((item) => item.org_code === orgCode) || null,
    [orgCode, organizationOptions]
  );

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    setOrgOptionsLoading(true);
    setOrgOptionsError("");

    fetch(`${API_BASE}/public/tenant-options`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then(async (res) => {
        const payload = await safeJson(res);
        if (!res.ok) {
          throw new Error(payload?.message || "Unable to load organizations.");
        }
        return payload || {};
      })
      .then((payload) => {
        if (!active) return;
        const options = (Array.isArray(payload.organizations) ? payload.organizations : [])
          .map((organization) => ({
            ...organization,
            org_code: String(organization?.org_code || "").trim().toLowerCase(),
          }))
          .filter((organization) => organization.org_code);
        setOrganizationOptions(options);

        setOrgCode((current) => {
          const cleanCurrent = String(current || "").trim().toLowerCase();
          if (cleanCurrent && options.some((organization) => organization.org_code === cleanCurrent)) {
            return cleanCurrent;
          }
          const preferredOrg = [
            payload.default_org_code,
            tenantContext?.tenant?.org_code,
            options[0]?.org_code,
          ]
            .map((value) => String(value || "").trim().toLowerCase())
            .find((value) => value && options.some((organization) => organization.org_code === value));

          return preferredOrg || current;
        });
      })
      .catch((err) => {
        if (!active || err?.name === "AbortError") return;
        setOrganizationOptions([]);
        setOrgOptionsError(err?.message || "Unable to load organizations.");
      })
      .finally(() => {
        if (active) setOrgOptionsLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [tenantContext?.tenant?.org_code]);

  useEffect(() => {
    const cleanOrgCode = String(orgCode || "").trim().toLowerCase();
    if (!cleanOrgCode || !selectedOrganization || typeof resolveTenantByOrgCode !== "function") {
      return undefined;
    }
    if (tenantOrgCode === cleanOrgCode) {
      return undefined;
    }

    const controller = new AbortController();
    resolveTenantByOrgCode(cleanOrgCode, { signal: controller.signal }).catch((err) => {
      if (err?.name !== "AbortError") {
        setOrgOptionsError(err?.message || "Unable to load organization style.");
      }
    });

    return () => controller.abort();
  }, [orgCode, resolveTenantByOrgCode, selectedOrganization, tenantOrgCode]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const cleanOrgCode = orgCode.trim();

    if (!cleanOrgCode && !tenantContext?.tenant?.org_code) {
      toast.error("Please select your organization.");
      return;
    }

    if (cleanOrgCode) {
      localStorage.setItem("last_login_org_code", cleanOrgCode);
    }

    dispatch(loginUser({
      employee_code: employeeCode,
      password,
      org_code: cleanOrgCode || tenantContext?.tenant?.org_code || undefined,
      tenant_key: cleanOrgCode ? undefined : tenantContext?.tenant?.key || undefined,
    }))
      .unwrap()
      .then((res) => {
        // Password accepted, but this tenant requires a second factor: the body
        // carries a challenge and no tokens. Switch to the code step.
        if (res?.mfa_required && !res?.access_token) {
          setMfaChallenge(res);
          setMfaCode("");
          setMfaError("");
          setTrustDevice(false);
          setMfaResendIn(Number(res.resend_available_in) || 60);
          return;
        }
        completeLogin(res);
      })
      .catch((err) => toast.error(err?.message || "Login failed"));
  };

  // Everything that happens once a real session payload arrives. Shared by the
  // straight password login and the MFA verify step so the landing logic below
  // exists exactly once.
  const completeLogin = useCallback((res) => {
        toast.success("Login successful!");
        const authResult = saveAuthFromResponse(res);

        const userRole = res?.effective_role || res?.role || res.user?.role || "employee";
        const isPlatformAdmin = res?.is_platform_admin === true;
        const permissions = Array.isArray(res?.permissions) ? res.permissions : [];
        const canReviewCv =
          userRole === "hr_admin" ||
          userRole === "recruiter" ||
          permissions.includes("*") ||
          permissions.includes("role.hr_admin") ||
          permissions.includes("role.recruiter");

        // Role-aware landing: management roles want their own dashboard, not
        // the CV page. CV review stays one click away in the sidebar.
        // Checks effective role, actual role, AND role.* permissions so users
        // whose display role differs from their canonical role (e.g. an
        // "HR Manager" with role.hr_admin) still land on the right dashboard.
        const dashboardForRole = (role, actualRole = "", perms = []) => {
          const normalize = (v) => String(v || "").trim().toLowerCase();
          const normalized = normalize(role);
          const normalizedActual = normalize(actualRole);
          const permRoles = perms
            .map((p) => normalize(p))
            .filter((p) => p.startsWith("role."))
            .map((p) => p.slice(5));
          const candidates = [normalized, normalizedActual, ...permRoles].filter(Boolean);
          const has = (...keys) => candidates.some((c) => keys.includes(c));

          if (has("hr_admin", "admin_hr")) return "/admin/dashboard";
          if (has("ceo")) return "/ceo/dashboard";
          if (has("executive", "executive_manager", "executive_director", "executive_managing_director")) {
            return "/ceo/dashboard";
          }
          if (has("manager", "head_of_department", "hod", "operation_manager", "operations_manager")) {
            return "/manager/dashboard";
          }
          // Finance shares the manager dashboard route; finance coordinators
          // share the employee dashboard route. There are no /finance or
          // /finance_coordinator dashboard pages today.
          if (has("finance")) return "/manager/dashboard";
          if (has("finance_coordinator")) return "/employee/dashboard";
          // Recruiters are staff members: land on their employee dashboard;
          // recruiter tools (Candidates, Employee CV) live in the sidebar.
          if (has("recruiter")) return "/employee/dashboard";
          return null;
        };

        const roleDashboard = dashboardForRole(userRole, res?.user?.actual_role || res?.actual_role, permissions);
        const targetPath = isPlatformAdmin
          ? "/super-admin/dashboard"
          : roleDashboard
            ? roleDashboard
            : canReviewCv
              ? "/cv/review"
              : "/cv/me";
        // Tour intro flags are scoped to CV pages today; only show the tour
        // when the user actually lands on one.
        const isCvLanding = targetPath === "/cv/review" || targetPath === "/cv/me";
        const tourSeenKey = canReviewCv
          ? CV_ADMIN_REVIEW_TOUR_SEEN_KEY
          : CV_TOUR_SEEN_KEY;
        const shouldForceTour =
          isCvLanding && !isPlatformAdmin && localStorage.getItem(tourSeenKey) !== "1";
        const finalPath = shouldForceTour ? `${targetPath}?tour=1` : targetPath;

        // When the same tab is reused for a different user (no explicit logout),
        // a soft navigate keeps stale Redux/component state from the previous
        // session. Force a full page load so the new identity starts clean.
        if (authResult?.isUserSwitch) {
          window.location.assign(finalPath);
        } else {
          navigate(finalPath);
        }
  }, [navigate]);

  // The org the password step used. Both MFA calls must carry it: the SPA sends
  // no tenant headers on /login/*, so this is the only thing telling the server
  // which tenant database to look in.
  const mfaOrgCode = (orgCode || tenantContext?.tenant?.org_code || "")
    .toString()
    .trim()
    .toLowerCase();

  const leaveMfaStep = () => {
    setMfaChallenge(null);
    setMfaCode("");
    setMfaError("");
    setMfaResendIn(0);
    setTrustDevice(false);
    setPassword("");
    dispatch(resetAuthStatus());
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    const code = mfaCode.trim();

    if (!/^\d{6}$/.test(code)) {
      setMfaError("Enter the 6-digit code from your email.");
      return;
    }

    setMfaLoading(true);
    setMfaError("");

    try {
      const data = await verifyMfaCode({
        challenge_id: mfaChallenge.challenge_id,
        code,
        remember_device: trustDevice,
        org_code: mfaOrgCode || undefined,
        tenant_key: mfaOrgCode ? undefined : tenantContext?.tenant?.key || undefined,
      });

      // Handed back exactly once; the server keeps only a hash.
      if (data?.mfa_device_token) {
        setMfaDeviceToken(data.mfa_device_token);
      }

      setMfaChallenge(null);
      completeLogin(data);
    } catch (err) {
      const status = err?.response?.status;
      const body = err?.response?.data;

      if (body?.mfa_restart) {
        toast.error(body.message || "Please sign in again.");
        leaveMfaStep();
        return;
      }

      if (status === 403) {
        toast.error(body?.message || "Your account is inactive.");
        leaveMfaStep();
        return;
      }

      setMfaError(
        body?.message ||
          // Network failures reject with a string, HTTP errors with an object.
          (typeof err === "string" ? err : err?.message) ||
          "That code is invalid or has expired."
      );
    } finally {
      setMfaLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (mfaResendIn > 0 || mfaLoading) return;

    setMfaLoading(true);
    setMfaError("");

    try {
      const data = await resendMfaCode({
        challenge_id: mfaChallenge.challenge_id,
        org_code: mfaOrgCode || undefined,
        tenant_key: mfaOrgCode ? undefined : tenantContext?.tenant?.key || undefined,
      });
      toast.success(data?.message || "We sent you a new code.");
      setMfaCode("");
      setMfaResendIn(Number(data?.resend_available_in) || 60);
    } catch (err) {
      const body = err?.response?.data;

      if (body?.mfa_restart) {
        toast.error(body.message || "Please sign in again.");
        leaveMfaStep();
        return;
      }

      if (body?.resend_available_in) {
        setMfaResendIn(Number(body.resend_available_in));
      }
      setMfaError(body?.message || "Could not send a new code. Try again shortly.");
    } finally {
      setMfaLoading(false);
    }
  };

  // Resend cooldown ticker.
  useEffect(() => {
    if (!inMfaStep || mfaResendIn <= 0) return undefined;
    const timer = setInterval(() => {
      setMfaResendIn((remaining) => (remaining > 0 ? remaining - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, [inMfaStep, mfaResendIn]);

  const handleForgotPassword = async () => {
    const identifier = fpIdentifier.trim();
    // WEB-005: prefer the dialog's own org input; fall back to login form / tenant context.
    const fpOrg = (fpOrgCode || orgCode || tenantContext?.tenant?.org_code || "")
      .toString()
      .trim()
      .toLowerCase();

    if (!fpOrg) {
      toast.error("Please select or enter your organization.");
      return;
    }

    if (!identifier) {
      toast.error("Please enter your email or employee code.");
      return;
    }

    // If it looks like an email, validate format (UX only)
    if (identifier.includes("@")) {
      const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier);
      if (!ok) {
        toast.error("Please enter a valid email.");
        return;
      }
    } else if (identifier.length < 3) {
      toast.error("Please enter a valid employee code.");
      return;
    }

    setFpLoading(true);

    // Optional timeout to avoid hanging UI
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);

    try {
      const res = await fetch(`${API_BASE}/auth/forgot-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          identifier,
          org_code: fpOrg,
        }),
        signal: controller.signal,
      });

      const data = await safeJson(res);

      if (!res.ok) {
        const msg =
          data?.message ||
          data?.error ||
          (res.status ? `Request failed (${res.status})` : "Request failed");
        throw new Error(msg);
      }

      toast.success(data?.message || "If the account exists, a reset link has been sent.");
      setFpOpen(false);
      setFpIdentifier("");
      setFpOrgCode("");
    } catch (e) {
      if (e?.name === "AbortError") {
        toast.error("Request timeout. Please try again.");
      } else {
        toast.error(e?.message || "Failed to send reset link.");
      }
    } finally {
      clearTimeout(timeoutId);
      setFpLoading(false);
    }
  };

  // Subtle parallax on mouse move
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      const dx = (e.clientX - cx) / cx;
      const dy = (e.clientY - cy) / cy;
      setTilt({ x: dx * 4, y: dy * 4 });
    };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  // HR-themed floaters
  const floaters = useMemo(() => {
    const palette = [
      { fg: brandPrimary, bg: hexToRgba(brandPrimary, 0.18) },
      { fg: brandSecondary, bg: hexToRgba(brandSecondary, 0.18) },
      { fg: brandAccent, bg: hexToRgba(brandAccent, 0.18) },
    ];

    const modules = [
      { icon: PeopleAltOutlinedIcon, label: "Employees", hint: "Directory" },
      { icon: FactCheckOutlinedIcon, label: "Appraisals", hint: "Reviews" },
      { icon: CalendarMonthOutlinedIcon, label: "Leave", hint: "Requests" },
      { icon: PaymentsOutlinedIcon, label: "Payroll", hint: "Payslips" },
      { icon: ChecklistOutlinedIcon, label: "Tasks", hint: "Approvals" },
      { icon: AssignmentTurnedInOutlinedIcon, label: "Compliance", hint: "Policies" },
      { icon: WorkOutlineOutlinedIcon, label: "Recruitment", hint: "Hiring" },
      { icon: TrendingUpOutlinedIcon, label: "KPIs", hint: "Insights" },
    ];

    const count = 10;
    return Array.from({ length: count }, (_, i) => {
      const m = modules[i % modules.length];
      const p = palette[i % palette.length];

      const top = 4 + Math.random() * 88;
      const left = 3 + Math.random() * 92;
      const scale = 0.85 + Math.random() * 0.45;
      const dur = 14 + Math.random() * 18;
      const delay = Math.random() * 7;
      const drift = 20 + Math.random() * 40;
      const rise = 18 + Math.random() * 38;
      const rot = -6 + Math.random() * 12;

      return { ...m, palette: p, top, left, scale, dur, delay, drift, rise, rot };
    });
  }, [brandAccent, brandPrimary, brandSecondary]);

  return (
    <Box sx={styles.page}>
      {/* Background */}
      <Box sx={styles.bgWrap} aria-hidden>
        <Box sx={styles.bgBase} />
        <Box sx={styles.grid} />
        <Box sx={styles.vignette} />

        {/* Workflow lines */}
        <Box sx={styles.workflowLayer}>
          <svg width="100%" height="100%" viewBox="0 0 1200 700" preserveAspectRatio="none" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wfStroke" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor={brandSecondary} stopOpacity="0.55" />
                <stop offset="55%" stopColor={brandPrimary} stopOpacity="0.45" />
                <stop offset="100%" stopColor={brandAccent} stopOpacity="0.55" />
              </linearGradient>
              <filter id="softGlow">
                <feGaussianBlur stdDeviation="2.2" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            <path
              d="M60,120 C240,60 360,240 520,190 C700,135 780,80 940,140 C1060,185 1100,290 1140,340"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="2"
            />
            <path
              d="M80,560 C260,480 360,640 520,560 C720,455 820,520 980,470 C1100,432 1120,360 1140,280"
              fill="none"
              stroke="rgba(255,255,255,0.08)"
              strokeWidth="2"
            />

            <path
              d="M60,120 C240,60 360,240 520,190 C700,135 780,80 940,140 C1060,185 1100,290 1140,340"
              fill="none"
              stroke="url(#wfStroke)"
              strokeWidth="2.5"
              strokeDasharray="10 14"
              filter="url(#softGlow)"
              className="wf-dash"
            />
            <path
              d="M80,560 C260,480 360,640 520,560 C720,455 820,520 980,470 C1100,432 1120,360 1140,280"
              fill="none"
              stroke="url(#wfStroke)"
              strokeWidth="2.5"
              strokeDasharray="10 14"
              filter="url(#softGlow)"
              className="wf-dash wf-dash2"
            />

            {[
              [60, 120],
              [300, 110],
              [520, 190],
              [760, 95],
              [940, 140],
              [1140, 340],
              [80, 560],
              [520, 560],
              [980, 470],
              [1140, 280],
            ].map(([x, y], idx) => (
              <g key={idx}>
                <circle cx={x} cy={y} r="5" fill="rgba(255,255,255,0.18)" />
                <circle cx={x} cy={y} r="2.6" fill={brandSecondary} opacity="0.65" className="wf-pulse" />
              </g>
            ))}
          </svg>
        </Box>

        {/* Floaters */}
        {floaters.map((f, i) => {
          const Icon = f.icon;
          return (
            <Box
              key={i}
              sx={{
                ...styles.floater,
                top: `${f.top}%`,
                left: `${f.left}%`,
                transform: `translate3d(0,0,0) scale(${f.scale}) rotate(${f.rot}deg)`,
                "--dur": `${f.dur}s`,
                "--delay": `${f.delay}s`,
                "--drift": `${f.drift}px`,
                "--rise": `${f.rise}px`,
                "--fg": f.palette.fg,
                "--bg": f.palette.bg,
              }}
            >
              <Box sx={styles.floaterInner}>
                <Box sx={styles.floaterIcon}>
                  <Icon sx={{ fontSize: 18, color: "var(--fg)" }} />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={styles.floaterTitle}>{f.label}</Typography>
                  <Typography sx={styles.floaterHint}>{f.hint}</Typography>
                </Box>
              </Box>
            </Box>
          );
        })}

        {/* Orbs */}
        <Box sx={{ ...styles.orb, ...styles.orbA }} />
        <Box sx={{ ...styles.orb, ...styles.orbB }} />
        <Box sx={{ ...styles.orb, ...styles.orbC }} />
      </Box>

      {/* Foreground */}
      <Box sx={styles.foreground}>
        <Container maxWidth="md" sx={{ py: 6 }}>
          <Box
            sx={{
              ...styles.shell,
              transform: `translate3d(${tilt.x}px, ${tilt.y}px, 0)`,
              transition: "transform 120ms ease-out",
            }}
          >
            {/* LEFT brand panel */}
            <Box sx={styles.leftPanel}>
              <CardMedia component="img" image={loginLogo} alt={`${brandName} logo`} sx={styles.logo} />

              <Typography sx={styles.portalTitle}>{brandName}</Typography>
              <Typography sx={styles.portalSub}>
                Secure access to {productLabel}: leave, payroll, appraisals, tasks, and more.
              </Typography>

              <Box sx={styles.leftBadges}>
                {[
                  { t: "Performance", c: brandSecondary },
                  { t: "Payroll", c: brandPrimary },
                  { t: "Compliance", c: brandAccent },
                ].map((x) => (
                  <Box key={x.t} sx={{ ...styles.tag, borderColor: `${x.c}55`, color: x.c }}>
                    {x.t}
                  </Box>
                ))}
              </Box>

              <Box sx={styles.leftFooter}>
                <Typography sx={styles.leftFooterText}>
                  Need help? Use “Forgot password” or contact HR support.
                </Typography>
              </Box>
            </Box>

            {/* RIGHT login form */}
            <Card sx={styles.formCard}>
              <CardContent sx={styles.formContent}>
                <Stack spacing={0.6} sx={{ mb: 2 }}>
                  <Typography sx={styles.formTitle}>
                    {inMfaStep ? "Check your email" : "Sign In"}
                  </Typography>
                  <Typography sx={styles.formSub}>
                    {inMfaStep
                      ? `We sent a 6-digit code to ${mfaChallenge?.delivery?.hint || "your work email"}.`
                      : "Use your employee code and password."}
                  </Typography>
                </Stack>

                {!inMfaStep && status === "failed" && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.message || "Invalid credentials."}
                  </Alert>
                )}

                {inMfaStep && mfaError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {mfaError}
                  </Alert>
                )}

                {inMfaStep ? (
                  <Box component="form" onSubmit={handleVerifyCode} noValidate>
                    <Stack spacing={2.1}>
                      <TextField
                        required
                        fullWidth
                        autoFocus
                        id="mfaCode"
                        label="6-digit code"
                        sx={styles.formField}
                        value={mfaCode}
                        onChange={(e) => {
                          setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                          if (mfaError) setMfaError("");
                        }}
                        inputProps={{
                          inputMode: "numeric",
                          maxLength: 6,
                          pattern: "[0-9]*",
                          // Lets iOS/Android offer the code straight from the inbox.
                          autoComplete: "one-time-code",
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlinedIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />

                      {mfaChallenge?.can_trust_device && (
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={trustDevice}
                              onChange={(e) => setTrustDevice(e.target.checked)}
                              size="small"
                            />
                          }
                          label={
                            <Typography sx={styles.footerHint}>
                              {`Trust this device for ${mfaChallenge?.trust_device_days || 30} days`}
                            </Typography>
                          }
                        />
                      )}

                      <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={mfaLoading}
                        sx={styles.signInBtn}
                      >
                        {mfaLoading ? (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <CircularProgress size={18} />
                            <span>Verifying...</span>
                          </Stack>
                        ) : (
                          "Verify & Sign In"
                        )}
                      </Button>

                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Link
                          component="button"
                          type="button"
                          disabled={mfaResendIn > 0 || mfaLoading}
                          onClick={handleResendCode}
                          sx={{
                            ...styles.linkBtn,
                            opacity: mfaResendIn > 0 || mfaLoading ? 0.5 : 1,
                            pointerEvents: mfaResendIn > 0 || mfaLoading ? "none" : "auto",
                          }}
                        >
                          {mfaResendIn > 0
                            ? `Resend in ${Math.floor(mfaResendIn / 60)}:${String(mfaResendIn % 60).padStart(2, "0")}`
                            : "Resend code"}
                        </Link>

                        {/* Without this a mistyped employee code strands the
                            user on the code step with no way back but a reload. */}
                        <Link component="button" type="button" onClick={leaveMfaStep} sx={styles.linkBtn}>
                          Use a different account
                        </Link>
                      </Stack>

                      <Divider />

                      <Typography sx={styles.footerHint}>
                        The code expires shortly. Never share it — {brandName} staff will never ask you for it.
                      </Typography>
                    </Stack>
                  </Box>
                ) : (
                <Box component="form" onSubmit={handleSubmit} noValidate>
                  <Stack spacing={2.1}>
                    {organizationOptions.length > 0 ? (
                      <SearchableSelect
                        label="Organization"
                        value={orgCode}
                        required
                        disabled={isLoading || orgOptionsLoading}
                        options={organizationOptions}
                        getOptionLabel={(organization) =>
                          organization?.display_name || organization?.short_name || organization?.org_code || ""
                        }
                        getOptionValue={(organization) => organization?.org_code || ""}
                        onChange={(value) => setOrgCode(String(value || "").trim().toLowerCase())}
                        helperText={
                          selectedOrganization?.product_label ||
                          selectedOrganization?.short_name ||
                          "Search and select the workspace you belong to."
                        }
                        textFieldProps={{ id: "organization", sx: styles.formField }}
                      />
                    ) : (
                      <TextField
                        required
                        fullWidth
                        id="orgCode"
                        label="Organization Code"
                        sx={styles.formField}
                        value={orgCode}
                        onChange={(e) => setOrgCode(e.target.value.trim().toLowerCase())}
                        autoComplete="organization"
                        helperText={
                          orgOptionsLoading
                            ? "Loading organizations..."
                            : orgOptionsError || "Enter your organization code."
                        }
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <WorkOutlineOutlinedIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}

                    <TextField
                      required
                      fullWidth
                      id="employeeCode"
                      label="Employee Code"
                      sx={styles.formField}
                      value={employeeCode}
                      onChange={(e) => setEmployeeCode(e.target.value)}
                      autoComplete="username"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BadgeOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />

                    <TextField
                      required
                      fullWidth
                      id="password"
                      label="Password"
                      sx={styles.formField}
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={showPass ? "Hide" : "Show"}>
                              <IconButton edge="end" onClick={() => setShowPass((s) => !s)} tabIndex={-1}>
                                {showPass ? (
                                  <VisibilityOffOutlinedIcon fontSize="small" />
                                ) : (
                                  <VisibilityOutlinedIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }}
                    />

                    <Button type="submit" variant="contained" size="large" disabled={isLoading} sx={styles.signInBtn}>
                      {isLoading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={18} />
                          <span>Signing in...</span>
                        </Stack>
                      ) : (
                        "Sign In"
                      )}
                    </Button>

                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Link
                        component="button"
                        type="button"
                        onClick={() => {
                          // WEB-005: prefill org from the main login form so user doesn't retype it
                          setFpOrgCode((current) => current || orgCode || tenantContext?.tenant?.org_code || "");
                          setFpOpen(true);
                        }}
                        sx={styles.linkBtn}
                      >
                        Forgot password?
                      </Link>
                    </Stack>

                    <Divider />

                    <Typography sx={styles.footerHint}>By signing in, you agree to internal HR usage policy.</Typography>
                  </Stack>
                </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>

      {/* Forgot Password dialog */}
      <Dialog open={fpOpen} onClose={() => !fpLoading && setFpOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HelpOutlineIcon fontSize="small" />
          Reset password
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ color: "#475569", fontSize: "0.95rem", mb: 2 }}>
            Enter your <b>organization</b> and your <b>email</b> or <b>employee code</b> to receive a password reset link.
          </Typography>

          <Stack spacing={2}>
            {/* WEB-005: Organization is required for forgot-password so the backend can scope the reset to the right tenant */}
            {organizationOptions.length > 0 ? (
              <SearchableSelect
                label="Organization"
                value={fpOrgCode}
                required
                disabled={fpLoading || orgOptionsLoading}
                options={organizationOptions}
                getOptionLabel={(organization) =>
                  organization?.display_name || organization?.short_name || organization?.org_code || ""
                }
                getOptionValue={(organization) => organization?.org_code || ""}
                onChange={(value) => setFpOrgCode(String(value || "").trim().toLowerCase())}
                helperText="Select the workspace you belong to."
                textFieldProps={{ id: "fpOrganization" }}
              />
            ) : (
              <TextField
                required
                fullWidth
                id="fpOrgCode"
                label="Organization Code"
                value={fpOrgCode}
                onChange={(e) => setFpOrgCode(e.target.value.trim().toLowerCase())}
                autoComplete="organization"
                helperText={
                  orgOptionsLoading
                    ? "Loading organizations..."
                    : orgOptionsError || "Enter your organization code."
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WorkOutlineOutlinedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <TextField
              fullWidth
              required
              label="Email or Employee Code"
              value={fpIdentifier}
              onChange={(e) => setFpIdentifier(e.target.value)}
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              helperText={`API: ${API_BASE}/auth/forgot-password`}
            />
          </Stack>

          <Alert severity="info" sx={{ mt: 2 }}>
            If the account exists in the system, you will receive a reset message.
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setFpOpen(false)} disabled={fpLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleForgotPassword}
            variant="contained"
            disabled={fpLoading}
            sx={styles.dialogActionBtn}
          >
            {fpLoading ? <CircularProgress size={18} /> : "Send link"}
          </Button>
        </DialogActions>
      </Dialog>

      <style>{keyframes}</style>
    </Box>
  );
}

const createLoginStyles = ({ primary, secondary, accent }) => ({
  page: {
    minHeight: "100vh",
    position: "relative",
    overflow: "hidden",
    backgroundColor: "#070c18",
  },
  bgWrap: { position: "absolute", inset: 0, zIndex: 0 },
  bgBase: {
    position: "absolute",
    inset: 0,
    background:
      `radial-gradient(1200px 800px at 12% 12%, ${hexToRgba(secondary, 0.33)}, rgba(0,0,0,0) 56%),` +
      `radial-gradient(900px 700px at 86% 24%, ${hexToRgba(primary, 0.40)}, rgba(0,0,0,0) 58%),` +
      `radial-gradient(950px 720px at 48% 92%, ${hexToRgba(accent, 0.18)}, rgba(0,0,0,0) 62%),` +
      "linear-gradient(180deg, #050913, #070c18 46%, #070c18)",
  },
  grid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.06) 1px, rgba(0,0,0,0) 1px)," +
      "linear-gradient(90deg, rgba(255,255,255,0.06) 1px, rgba(0,0,0,0) 1px)",
    backgroundSize: "78px 78px",
    maskImage: "radial-gradient(circle at 50% 35%, black 32%, transparent 72%)",
    opacity: 0.22,
  },
  vignette: {
    position: "absolute",
    inset: 0,
    background: "radial-gradient(circle at 50% 30%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.52) 86%)",
  },
  workflowLayer: { position: "absolute", inset: 0, opacity: 0.85, pointerEvents: "none" },

  floater: {
    position: "absolute",
    pointerEvents: "none",
    animationName: "hrFloat",
    animationTimingFunction: "ease-in-out",
    animationIterationCount: "infinite",
    animationDuration: "var(--dur)",
    animationDelay: "var(--delay)",
    filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.35))",
  },
  floaterInner: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.06))",
    backdropFilter: "blur(8px)",
    minWidth: 180,
  },
  floaterIcon: {
    width: 34,
    height: 34,
    borderRadius: 12,
    display: "grid",
    placeItems: "center",
    background: "var(--bg)",
    border: "1px solid rgba(255,255,255,0.10)",
  },
  floaterTitle: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.2,
    color: "rgba(255,255,255,0.92)",
    lineHeight: 1.1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  floaterHint: {
    fontSize: 11,
    color: "rgba(255,255,255,0.62)",
    lineHeight: 1.1,
    mt: 0.3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },

  orb: {
    position: "absolute",
    borderRadius: 999,
    filter: "blur(42px)",
    opacity: 0.5,
    pointerEvents: "none",
    animation: "orbBreath 10s ease-in-out infinite",
  },
  orbA: {
    width: 520,
    height: 520,
    left: "-110px",
    top: "10%",
    background: `radial-gradient(circle at 30% 30%, ${hexToRgba(secondary, 0.55)}, rgba(0,0,0,0) 62%)`,
  },
  orbB: {
    width: 520,
    height: 520,
    right: "-160px",
    top: "18%",
    background: `radial-gradient(circle at 30% 30%, ${hexToRgba(primary, 0.55)}, rgba(0,0,0,0) 62%)`,
    animationDelay: "1.5s",
  },
  orbC: {
    width: 560,
    height: 560,
    left: "28%",
    bottom: "-220px",
    background: `radial-gradient(circle at 30% 30%, ${hexToRgba(accent, 0.45)}, rgba(0,0,0,0) 65%)`,
    animationDelay: "2.4s",
  },

  foreground: { position: "relative", zIndex: 2, minHeight: "100vh", display: "flex", alignItems: "center" },

  shell: {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: "1.05fr 0.95fr" },
    gap: 2,
    alignItems: "stretch",
  },

  leftPanel: {
    borderRadius: 4,
    border: "1px solid rgba(255,255,255,0.14)",
    background: "linear-gradient(180deg, rgba(11,20,38,0.78), rgba(11,20,38,0.55))",
    backdropFilter: "blur(10px)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.25)",
    p: { xs: 3, md: 4 },
    display: "flex",
    flexDirection: "column",
    minHeight: { xs: "auto", md: 460 },
  },

  logo: {
    width: 170,
    height: "auto",
    mx: { xs: "auto", md: 0 },
    mb: 2,
    filter: "drop-shadow(0 10px 18px rgba(0,0,0,0.20))",
  },

  portalTitle: {
    fontWeight: 1000,
    letterSpacing: 0.2,
    color: "rgba(255,255,255,0.92)",
    fontSize: { xs: "1.35rem", md: "1.55rem" },
    textAlign: { xs: "center", md: "left" },
  },
  portalSub: {
    mt: 1,
    color: "rgba(255,255,255,0.66)",
    fontSize: "0.98rem",
    lineHeight: 1.55,
    textAlign: { xs: "center", md: "left" },
  },

  leftBadges: {
    mt: 2.2,
    display: "flex",
    gap: 1,
    flexWrap: "wrap",
    justifyContent: { xs: "center", md: "flex-start" },
  },
  tag: {
    px: 1.2,
    py: 0.6,
    borderRadius: 999,
    border: "1px solid",
    background: "rgba(255,255,255,0.06)",
    fontWeight: 900,
    fontSize: "0.85rem",
  },

  leftFooter: { mt: "auto", pt: 2.5 },
  leftFooterText: {
    color: "rgba(255,255,255,0.55)",
    fontSize: "0.9rem",
    lineHeight: 1.5,
    textAlign: { xs: "center", md: "left" },
  },

  formCard: {
    borderRadius: 4,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.88), rgba(255,255,255,0.95))",
    backdropFilter: "blur(10px)",
    boxShadow: "0 18px 60px rgba(0,0,0,0.35)",
  },
  formContent: { p: { xs: 3, md: 4 } },
  formTitle: { fontWeight: 1000, color: "#0f172a", fontSize: "1.35rem" },
  formSub: { color: "#475569", fontSize: "0.98rem" },
  formField: {
    "& .MuiInputLabel-root": {
      color: "#334155",
      fontWeight: 800,
      opacity: 1,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: secondary,
    },
    "& .MuiInputLabel-root.Mui-disabled": {
      color: "#475569",
      opacity: 1,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: "rgba(255,255,255,0.72)",
      color: "#0f172a",
      "& fieldset": {
        borderColor: "rgba(100,116,139,0.42)",
      },
      "&:hover fieldset": {
        borderColor: "rgba(71,85,105,0.68)",
      },
      "&.Mui-focused fieldset": {
        borderColor: secondary,
        borderWidth: 2,
      },
      "&.Mui-disabled": {
        backgroundColor: "rgba(248,250,252,0.92)",
        color: "#334155",
      },
      "&.Mui-disabled fieldset": {
        borderColor: "rgba(148,163,184,0.48)",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "#0f172a",
      fontWeight: 700,
      WebkitTextFillColor: "#0f172a",
      "&::placeholder": {
        color: "#64748b",
        opacity: 1,
      },
    },
    "& .MuiOutlinedInput-input.Mui-disabled": {
      color: "#334155",
      WebkitTextFillColor: "#334155",
      opacity: 1,
    },
    "& .MuiInputAdornment-root .MuiSvgIcon-root": {
      color: "#64748b",
    },
    "& .MuiIconButton-root": {
      color: "#64748b",
    },
    "& .MuiFormHelperText-root": {
      color: "#475569",
      fontWeight: 700,
      mt: 0.75,
    },
  },

  signInBtn: {
    py: 1.25,
    borderRadius: 2.4,
    fontWeight: 1000,
    textTransform: "none",
    background: `linear-gradient(90deg, ${primary}, ${secondary})`,
    boxShadow: `0 12px 30px ${hexToRgba(primary, 0.28)}`,
    "&:hover": {
      background: `linear-gradient(90deg, ${primary}, ${secondary})`,
      filter: "brightness(1.03)",
      boxShadow: `0 14px 34px ${hexToRgba(primary, 0.33)}`,
    },
    "&:disabled": { opacity: 0.7 },
  },

  dialogActionBtn: {
    background: `linear-gradient(90deg, ${primary}, ${secondary})`,
    fontWeight: 900,
    textTransform: "none",
    "&:hover": {
      background: `linear-gradient(90deg, ${primary}, ${secondary})`,
      filter: "brightness(1.03)",
    },
  },

  linkBtn: {
    color: secondary,
    fontWeight: 800,
    fontSize: "0.92rem",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },

  footerHint: { textAlign: "center", fontSize: "0.88rem", color: "#64748b" },
});

const keyframes = `
@keyframes hrFloat {
  0%   { transform: translate3d(0,0,0) scale(var(--scale, 1)) rotate(var(--rot, 0deg)); }
  50%  { transform: translate3d(var(--drift), calc(var(--rise) * -1), 0) scale(calc(var(--scale, 1) * 1.03)) rotate(var(--rot, 0deg)); }
  100% { transform: translate3d(0,0,0) scale(var(--scale, 1)) rotate(var(--rot, 0deg)); }
}
@keyframes orbBreath {
  0%,100% { transform: translate3d(0,0,0) scale(1); opacity: 0.46; }
  50% { transform: translate3d(0,-12px,0) scale(1.05); opacity: 0.60; }
}
.wf-dash { animation: dashMove 8.5s linear infinite; }
.wf-dash2 { animation-duration: 11.5s; animation-direction: reverse; opacity: 0.7; }
@keyframes dashMove {
  0% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: -220; }
}
.wf-pulse { transform-origin: center; animation: nodePulse 2.8s ease-in-out infinite; }
@keyframes nodePulse {
  0%,100% { opacity: 0.35; r: 2.6; }
  50% { opacity: 0.85; r: 3.6; }
}
`;

export default Login;
